import React from 'react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import DialogContentText from '@mui/material/DialogContentText'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Skeleton from '@mui/material/Skeleton'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'
import ReplayIcon from '@mui/icons-material/Replay'
import makeStyles from '@mui/styles/makeStyles'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import moment from 'moment-timezone'

import { useHistory, useParams } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'

import { FirebaseContext } from '../../utils/firebase'
import 'firebase/auth'

import { getFrequencyFromUnixCronSchedule } from '../../utils/helpers'
import { API_ROOT_URL } from '../../constants/'
import * as ROUTES from '../../constants/routes'

import voluumLogoImg from '../../media/voluum_logo.png'


const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    left: 66,
    right: 0,
    width: 'calc(100% - 66px)',
    height: '100vh',
    overflowY: 'auto',
    backgroundColor: theme.palette.grey[50],
  },
  logoImg: {
    width: 100,
  },
  redButton: {
    color: theme.palette.error.main,
    '&:hover': {
      background: 'rgba(255,23,68,0.04)',
    },
  },
  formControl: {
    margin: theme.spacing(1),
  },
}))

const REVOKE_SUCCESS_MESSAGE = 'Account removed. Data will no longer be synced from this account. Redirecting back to settings...'
const SYNC_SUCCESS_MESSAGE = 'Sync initialized. Processing may take a while, and you can safely leave this page.'
const SYNC_ERROR_MESSAGE = 'Sync failed to initialize. Try again and contact support if error persists.'
const CONFIG_MAP = {
  workspaceId: {
    display: 'Workspace ID',
    type: 'text',
    default: '',
    placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
  },
  columns: {
    display: 'Columns (comma-separated)',
    type: 'text',
    default: '',
    placeholder: 'customVariable2, customVariable4, revenue, clickId, postbackTimestamp',
  },
}

const dateRangeMenuItems = [
  { value: 'last_7_days', display: 'Last 7 days'},
  { value: 'last_30_days', display: 'Last 30 days'},
  { value: 'last_3_months', display: 'Last 3 months'},
  { value: 'last_6_months', display: 'Last 6 months'},
  { value: 'last_12_months', display: 'Last 12 months'},
  { value: 'last_2_years', display: 'Last 2 years'},
  { value: 'last_3_years', display: 'Last 3 years'},
  { value: 'ytd', display: 'Year to date'},
  { value: 'last_and_this_year', display: 'Last year and this year'},
  { value: 'all_time', display: 'All time'},
  { value: 'custom', display: 'Custom'},
]

const syncFrequencyMenuItems = [
  { value: 'hourly', display: 'Hourly'},
  { value: 'daily', display: 'Daily'},
  { value: 'weekly', display: 'Weekly'},
  { value: 'monthly', display: 'Monthly'},
]

const VoluumSource = () => {
  const classes = useStyles()
  const { id } = useParams()
  const history = useHistory()
  const firebase = React.useContext(FirebaseContext)

  const [timezone, setTimezone] = React.useState(moment.tz.guess())
  const [message, setMessage] = React.useState('')
  const [dateRange, setDateRange] = React.useState('last_2_years')
  const [showMessage, setShowMessage] = React.useState(false)
  const [messageSeverity, setMessageSeverity] = React.useState('success')
  const [showRevokeConfirmation, setShowRevokeConfirmation] = React.useState(false)

  const [startDate, setStartDate] = React.useState('')
  const [endDate, setEndDate] = React.useState('')
  const [syncFrequency, setSyncFrequency] = React.useState('daily')

  const [config, setConfig] = React.useState(() => {
    const initialConfig = {}
    Object.keys(CONFIG_MAP).forEach(key => {
      initialConfig[key] = CONFIG_MAP[key].default
    })
    return initialConfig
  })

  // Maintain config local state for text fields to handle unsaved changes
  const textFieldKeys = Object.keys(CONFIG_MAP).filter(key => CONFIG_MAP[key].type === 'text')
  const [localTextValues, setLocalTextValues] = React.useState(() =>
    Object.fromEntries(textFieldKeys.map(key => [key, config[key] || '']))
  )
  const [dirtyFields, setDirtyFields] = React.useState(() =>
    Object.fromEntries(textFieldKeys.map(key => [key, false]))
  )

  // Keep local state in sync with config changes
  React.useEffect(() => {
    // eslint-disable-next-line no-unused-vars
    setLocalTextValues(prev =>
      Object.fromEntries(textFieldKeys.map(key => [key, config[key] || '']))
    )
    // eslint-disable-next-line no-unused-vars
    setDirtyFields(prev =>
      Object.fromEntries(textFieldKeys.map(key => [key, false]))
    )
  }, [config])

  // Recompute when the timezone changes, not just the range. `timezone` starts as the browser's
  // guessed zone and only becomes the integration's zone once the integration loads. Without
  // `timezone` in the deps, the range is computed once in the browser zone and then rendered in the
  // integration zone, which pushes endOf('day') across midnight (e.g. the End Date shows tomorrow).
  // Recomputing when the real timezone arrives keeps the range correct. Custom ranges are untouched.
  React.useEffect(() => {
    if (dateRange !== 'custom') {
      setDatesFromRange(dateRange)
    }
  }, [dateRange, timezone])

  const {
    isLoading: isLoadingIntegration,
    data: integration = {}
  } = useQuery(['integration-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 15 * 60 * 1000,  // 15 minutes
      staleTime: 15 * 60 * 1000,  // 15 minutes
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data.timezone) {
          setTimezone(data.timezone)
        }
        if (data.schedule) {
          const frequency = getFrequencyFromUnixCronSchedule(data.schedule)
          setSyncFrequency(frequency)
        }
        if (data.config) {
          const integrationConfig = JSON.parse(data.config)
          if (Object.keys(integrationConfig).length > 0) {
            setConfig({...config, ...integrationConfig})
          }
        }
      }
    },
  )

  const {
    isLoading: isLoadingIntegrationJob,
    data: integrationJob = {},
    refetch: refetchIntegrationJob
  } = useQuery(['integration-job-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/${id}/job`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 15 * 60 * 1000,  // 15 minutes
      staleTime: 15 * 60 * 1000,  // 15 minutes
      refetchOnWindowFocus: false,
    }
  )

  const { isFetching: isSyncing, refetch: sync } = useQuery(['integration-sync', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/voluum/sync`, {
        method: 'POST',
        body: JSON.stringify({
          integration_id: id,
          startDate,
          endDate,
          syncFrequency,
          timezone,
        }),
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      }).then(res => res.json())
    }),
    {
      enabled: false,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      staleTime: 0,
      onSuccess: () => {
        setShowMessage(true)
        setMessage(SYNC_SUCCESS_MESSAGE)
        setMessageSeverity('success')
        refetchIntegrationJob()
      },
      onError: () => {
        setShowMessage(true)
        setMessage(SYNC_ERROR_MESSAGE)
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isUpdatingSyncFrequency, mutate: updateSyncFrequency } = useMutation(
    newSyncFrequency => firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/voluum/sync`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          integration_id: id,
          syncFrequency: newSyncFrequency
        }),
      }).then(res => res.json())
    }),
    {
      onSuccess: () => {
        setShowMessage(true)
        setMessage('Sync frequency successfully updated')
        setMessageSeverity('success')
        refetchIntegrationJob()
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error updating sync frequency. Please try again, and if problem persists, contact support.')
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isUpdatingTimezone, mutate: updateTimezone } = useMutation(
    newTimezone => firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/voluum/sync`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          integration_id: id,
          timezone: newTimezone
        }),
      }).then(res => res.json())
    }),
    {
      onSuccess: () => {
        setShowMessage(true)
        setMessage('Timezone successfully updated')
        setMessageSeverity('success')
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error updating timezone. Please try again, and if problem persists, contact support.')
        setMessageSeverity('error')
      }
    }
  )

  const revoke = () => {
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/voluum/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          integration_id: id
        })
      }).then(res => {
        setShowRevokeConfirmation(false)
        setMessage(REVOKE_SUCCESS_MESSAGE)
        setShowMessage(true)
        setTimeout(() => history.push(ROUTES.SETTINGS), 3000)
        return res.json()
      })
    })
  }

  const handleCloseMessage = (_, reason) => {
    if (reason === 'clickaway') return
    setShowMessage(false)
  }

  const handleChangeTimezone = (timezone) => {
    if (!timezone) return
    setTimezone(timezone)
    updateTimezone(timezone)
  }

  const handleChangeSyncFrequency = (frequency) => {
    setSyncFrequency(frequency)
    updateSyncFrequency(frequency)
  }

  const setDatesFromRange = (dateRange) => {
    let startDate, endDate
    switch (dateRange) {
      case 'last_7_days':
        startDate = moment.tz(timezone).startOf('day').subtract(7, 'days').toISOString()
        endDate = moment.tz(timezone).endOf('day').toISOString()
        break
      case 'last_30_days':
        startDate = moment.tz(timezone).startOf('day').subtract(30, 'days').toISOString()
        endDate = moment.tz(timezone).endOf('day').toISOString()
        break
      case 'last_3_months':
        startDate = moment.tz(timezone).startOf('day').subtract(3, 'months').toISOString()
        endDate = moment.tz(timezone).endOf('day').toISOString()
        break
      case 'last_6_months':
        startDate = moment.tz(timezone).startOf('day').subtract(6, 'months').toISOString()
        endDate = moment.tz(timezone).endOf('day').toISOString()
        break
      case 'last_12_months':
        startDate = moment.tz(timezone).startOf('day').subtract(12, 'months').toISOString()
        endDate = moment.tz(timezone).endOf('day').toISOString()
        break
      case 'last_2_years':
        startDate = moment.tz(timezone).startOf('day').subtract(2, 'years').toISOString()
        endDate = moment.tz(timezone).endOf('day').toISOString()
        break
      case 'last_3_years':
        startDate = moment.tz(timezone).startOf('day').subtract(3, 'years').toISOString()
        endDate = moment.tz(timezone).endOf('day').toISOString()
        break
      case 'ytd':
        startDate = moment.tz(timezone).startOf('year').startOf('day').toISOString()
        endDate = moment.tz(timezone).endOf('day').toISOString()
        break
      case 'last_and_this_year':
        startDate = moment.tz(timezone).subtract(1, 'year').startOf('year').startOf('day').toISOString()
        endDate = moment.tz(timezone).endOf('day').toISOString()
        break
      case 'all_time':
        startDate = moment.tz('1970', timezone).startOf('day').toISOString()
        endDate = moment.tz(timezone).endOf('day').toISOString()
        break
      default:
        startDate = moment.tz(timezone).startOf('day').subtract(12, 'months').toISOString()
        endDate = moment.tz(timezone).endOf('day').toISOString()
        break
    }
    setStartDate(startDate)
    setEndDate(endDate)
  }

  const handleCustomDateChange = (date, type) => {
    let newDate = moment.tz(date, timezone).startOf('day').toISOString()
    if (type === 'startDate') {
      if (moment.tz(newDate, timezone).isAfter(endDate)) {
        setEndDate(newDate)
      }
      setStartDate(newDate)
    } else if (type === 'endDate'){
      if (moment.tz(newDate, timezone).isBefore(startDate)) {
        setStartDate(newDate)
      }
      setEndDate(newDate)
    }
  }


  const { isLoading: isUpdatingConfig, mutate: updateConfig } = useMutation(
    ({ key, value }) => firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/${id}/config`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          key,
          value,
        }),
      }).then(res => res.json())
    }),
    {
      onMutate: ({ key, value }) => {
        // Optimistically update the config state
        const previousValue = config[key]
        setConfig(prevConfig => ({
          ...prevConfig,
          [key]: value
        }))
        return { previousValue }
      },
      onSuccess: () => {
        setShowMessage(true)
        setMessage('Data Source successfully updated')
        setMessageSeverity('success')
      },
      onError: (error, { key }, context) => {
        console.error(`Error updating config for key ${key}:`, error)
        const previousValue = context.previousValue
        // Revert the optimistic update if the mutation fails
        if (!previousValue) {
          console.error(`No previous value found for key ${key}. Unable to revert optimistic update.`)
          return
        }
        // Revert the optimistic update
         setConfig(prevConfig => ({
          ...prevConfig,
          [key]: previousValue
        }))
        setShowMessage(true)
        setMessage('Error updating settings. Please try again, and if problem persists, contact support.')
        setMessageSeverity('error')
      }
    }
  )

  return (
    <div className={classes.root}>
      <Box padding={2}>
        <Button
          variant='outlined'
          onClick={() => history.push(ROUTES.SETTINGS)}
          size='small'
        >
          Back
        </Button>

        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={2}>
                  <img
                    className={classes.logoImg}
                    alt='voluum_logo'
                    src={voluumLogoImg}
                  />
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='h5' gutterBottom>
                    Voluum Source
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Name */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Name
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box height='100%' display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      {isLoadingIntegration ? <Skeleton variant='text' width={100} /> : `${integration.nickname}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Access Key ID */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Access Key ID
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box height='100%' display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      {isLoadingIntegration ? <Skeleton variant='text' width={100} /> : `${integration.platform_id}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Access Key */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Access Key
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box height='100%' display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      {isLoadingIntegration ? <Skeleton variant='text' width={100} /> : `${integration.api_key.slice(0, 4)}****************${integration.api_key.slice(-4)}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Timezone Selector */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Timezone
                  </Typography>
                </Grid>

                <Grid item xs={10}>
                  <Typography variant='body2' gutterBottom>
                    Select the timezone that matches the timezone set in your Voluum account. This ensures that date and time data is consistent between Voluum and LTV Numbers.
                  </Typography>
                  <Typography variant='body2' gutterBottom>
                    It is strongly recommended that you do NOT change this value in your Voluum account after setting it below, or it can cause date and time inconsistencies that could affect the integrity of the data.
                  </Typography>

                  <Box marginTop={0} display='flex' alignItems='center'>
                    <FormControl
                      variant='outlined'
                      margin='dense'
                      sx={{ margin: 1, minWidth: 300 }}
                      required
                     >
                      {isLoadingIntegration ? <Skeleton variant='rect' width={300} /> : (
                        <Autocomplete
                          id='timezone'
                          options={moment.tz.names()}
                          value={timezone}
                          onChange={event => handleChangeTimezone(event.target.textContent)}
                          renderInput={(params) => <TextField {...params} label='Timezone' />}
                          loading={isUpdatingTimezone}
                        />
                      )}
                    </FormControl>
                  </Box>

                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Sync */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Sync
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='body2' paragraph>
                    To sync payment data from Voluum with LTV Numbers, select the appropriate data range and click the Sync button below. This will start a process of retrieving data from the account and loading it into LTV Numbers. Due to rate limitations inside Voluum, this process can take anywhere from several minutes to several hours.
                  </Typography>

                  <Box marginTop={2} display='flex' alignItems='center'>
                    <FormControl
                      variant='outlined'
                      margin='dense'
                      sx={{ margin: 1, minWidth: 150 }}
                      required
                     >
                      <InputLabel id='data-type-label'>Date Range</InputLabel>
                      <Select
                        labelId='date-range-label'
                        id='date-range-select'
                        value={dateRange}
                        onChange={event => setDateRange(event.target.value)}
                        label='Date Range'
                      >
                        {dateRangeMenuItems.map(item =>
                          <MenuItem key={item.value} value={item.value}>{item.display}</MenuItem>
                        )}
                      </Select>
                    </FormControl>

                    {isLoadingIntegration ? (
                      <Skeleton variant='text' width={200} />
                    ) : (
                      <>
                        <FormControl
                          variant='outlined'
                          margin='dense'
                          sx={{ margin: 1, minWidth: 150 }}
                          required
                        >
                          <InputLabel id='data-type-label'>Sync Frequency</InputLabel>
                          <Select
                            labelId='sync-frequency-label'
                            id='sync-frequency-select'
                            value={syncFrequency}
                            onChange={event => handleChangeSyncFrequency(event.target.value)}
                            label='Sync Frequency'
                          >
                            {syncFrequencyMenuItems.map(item =>
                              <MenuItem key={item.value} value={item.value}>{item.display}{isUpdatingSyncFrequency && <>&nbsp;<CircularProgress color='inherit' size={16}/></>}</MenuItem>
                            )}
                          </Select>
                        </FormControl>

                        {isLoadingIntegrationJob ? (
                            <Skeleton variant='text' width={200} />
                          ) : (
                            integrationJob.job ? (
                              <Box>
                                <Typography variant='caption' display='block'>
                                  <b>Last sync:</b> {integrationJob.job.lastAttemptTime ? moment(integrationJob.job.lastAttemptTime.seconds * 1000).toLocaleString() : 'None'}
                                </Typography>
                                <Typography variant='caption' display='block'>
                                  <b>Next sync:</b> {integrationJob.job.scheduleTime ? moment(integrationJob.job.scheduleTime.seconds * 1000).toLocaleString() : 'None'}
                                </Typography>
                              </Box>
                            ) : (
                              <Box>
                                <Typography variant='caption' display='block'>
                                  Sync job not yet created. Create sync job by syncing the data or updating the sync frequency.
                                </Typography>
                              </Box>
                            )
                          )
                        }
                      </>
                    )}
                  </Box>

                  {dateRange === 'custom' && (
                    <Box marginTop={2} display='flex' alignItems='center' columnGap={2}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          label='Start Date'
                          value={moment(startDate)}
                          disableFuture
                          timezone={timezone}
                          onChange={(date) => handleCustomDateChange(date, 'startDate')}
                          slotProps={{
                            textField: {
                              size: 'small',
                            }
                          }}
                        />
                        <DatePicker
                          label='End Date'
                          value={moment(endDate)}
                          disableFuture
                          timezone={timezone}
                          onChange={(date) => handleCustomDateChange(date, 'endDate')}
                          slotProps={{
                            textField: {
                              size: 'small',
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                  )}

                  <Box marginTop={2}>
                    <Typography variant='body2'>
                      <b>Date range to sync:</b> {moment.tz(startDate, timezone).format('MM/DD/YYYY')} to {moment.tz(endDate, timezone).format('MM/DD/YYYY')}
                    </Typography>
                  </Box>

                  <Box marginTop={2}>
                    <Button
                      className={classes.syncButton}
                      variant='contained'
                      color='secondary'
                      onClick={() => sync()}
                    >
                      {isSyncing ?
                        <CircularProgress color='inherit' size={24} className={classes.buttonProgress} />
                        : 'Sync Data'
                      }
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Config */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Settings
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='body2' paragraph>
                    Configure the Voluum integration fields below. These values are required for correct operation and can be updated at any time. All fields are stored in the integration config.
                  </Typography>

                  <Typography variant='body2' paragraph>
                    <b>Workspace ID:</b> This is the unique identifier for your Voluum workspace. Note that you must be logged in with an Admin account to find this. Go to settings, then select <b>Collaboration tools</b> then you will see a list of all workspaces. Find the relevant <b>Workspace id</b> and enter it below. It will look like this: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx.
                  </Typography>

                  <Typography variant='body2' paragraph>
                    <b>Columns:</b> Specify the columns to include in the conversions log sync. This should be a comma-separated list of column names from your Voluum account (e.g., customVariable2, customVariable4, revenue, clickId, postbackTimestamp). Each row returned is a single conversion — no grouping is needed. You must include <b>clickId</b>, <b>postbackTimestamp</b>, and <b>revenue</b> for the sync to work correctly. Add any custom variable columns that contain data you want to track (email, funnel name, etc.).
                  </Typography>

                  {isLoadingIntegration ? <Skeleton variant='rect' width={300} /> : (
                    <Box marginTop={2} display='flex' alignItems='flex-start' flexDirection='column'>

                      {Object.keys(CONFIG_MAP).map(key => {
                        const configItem = CONFIG_MAP[key]
                        if (configItem.type === 'select') {
                          return (
                            <FormControl
                              variant='outlined'
                              size='small'
                              margin='dense'
                              sx={{ margin: 1, minWidth: 400 }}
                              key={key}
                              required
                            >
                              <InputLabel id={`${key}-label`}>{configItem.display}</InputLabel>
                              <Select
                                labelId={`${key}-label`}
                                id={`${key}-select`}
                                value={config[key]}
                                onChange={event => updateConfig({ key, value: event.target.value })}
                                label={configItem.display}
                              >
                                {configItem.options.map(option =>
                                  <MenuItem key={option.value} value={option.value}>{option.display}</MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          )
                        } else if (configItem.type === 'text') {
                          return (
                            <TextField
                              key={key}
                              label={configItem.display}
                              name={key}
                              value={localTextValues[key] || ''}
                              onChange={event => {
                                const value = event.target.value
                                setLocalTextValues(prev => ({ ...prev, [key]: value }))
                                setDirtyFields(prev => ({ ...prev, [key]: value !== (config[key] || '') }))
                              }}
                              placeholder={configItem.placeholder}
                              helperText={dirtyFields[key] ? 'Unsaved changes' : ''}
                              variant='outlined'
                              size='small'
                              fullWidth
                              sx={{ margin: 1 }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position='end'>
                                    {dirtyFields[key] && (
                                      <>
                                        <IconButton
                                          color='inherit'
                                          onClick={() => {
                                            setLocalTextValues(prev => ({ ...prev, [key]: config[key] || '' }))
                                            setDirtyFields(prev => ({ ...prev, [key]: false }))
                                          }}
                                          aria-label='revert'
                                          size='small'
                                          sx={{
                                            width: 28,
                                            height: 28,
                                            marginRight: 0.5,
                                          }}
                                        >
                                          <ReplayIcon fontSize='small' />
                                        </IconButton>
                                        <Button
                                          color='primary'
                                          variant='contained'
                                          disabled={!dirtyFields[key]}
                                          onClick={() => {
                                            updateConfig({ key, value: localTextValues[key] })
                                            setDirtyFields(prev => ({ ...prev, [key]: false }))
                                          }}
                                          edge='end'
                                          aria-label='save'
                                          sx={{
                                            minWidth: 0,
                                            width: 28,
                                            height: 28,
                                            padding: 0,
                                          }}
                                        >
                                          <CheckIcon fontSize='small' />
                                        </Button>
                                      </>
                                    )}
                                  </InputAdornment>
                                )
                              }}
                            />
                          )
                        }
                        return null
                      })}

                      {isUpdatingConfig && (
                        <CircularProgress
                          className={classes.buttonProgress}
                          color='inherit'
                          size={24}
                          sx={{ marginLeft: 2 }}
                        />
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Delete
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='body2' gutterBottom>
                    If you want to remove this data integration so payments are no longer synced, click the button below.
                  </Typography>
                  <Button
                    className={classes.redButton}
                    variant='text'
                    onClick={() => setShowRevokeConfirmation(true)}
                  >
                    Delete Source
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Box>
      <Dialog
        open={showRevokeConfirmation}
        onClose={() => setShowRevokeConfirmation(false)}
      >
        <DialogTitle>
          Remove access to account <b>&quot;{integration.nickname}&quot;</b>?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will prevent data from being synced to LTV Numbers.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.redButton}
            variant='text'
            onClick={() => setShowRevokeConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            onClick={() => revoke()}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        autoHideDuration={10000}
        onClose={handleCloseMessage}
      >
        <Alert
          severity={messageSeverity}
          variant='filled'
          onClose={handleCloseMessage}
        >
          {message}
        </Alert>
      </Snackbar>
    </div>
  )
}

VoluumSource.propTypes = {}

export default VoluumSource